import { useEffect, useRef } from 'react';
import type { DroneTelemetry } from '@shared/types';
import { useTelemetryAnimationFrame } from '../../../drone/hooks';

interface AttitudeBallProps {
  droneId: string | undefined;
  /** store 中的兜底帧：链路刚建立或断开时仍要显示最后已知姿态 */
  telemetry: DroneTelemetry | null;
  size?: number;
  className?: string;
}

/**
 * 姿态球。
 *
 * 直接改 SVG 变换，不经过 React 状态：姿态是刷新率最高的数据，
 * 一旦走 setState，20 Hz 的遥测就会变成整棵树的 20 次重渲染。
 */
export function AttitudeBall({ droneId, telemetry, size = 232, className = '' }: AttitudeBallProps) {
  const ballRef = useRef<SVGGElement>(null);
  const pitchRef = useRef<SVGGElement>(null);
  const needleRef = useRef<SVGGElement>(null);
  const yawTextRef = useRef<SVGTSpanElement>(null);
  const rollTextRef = useRef<HTMLSpanElement>(null);
  const pitchTextRef = useRef<HTMLSpanElement>(null);
  const rateTextRef = useRef<HTMLSpanElement>(null);

  const half = size / 2;
  const radius = half - 12;
  /** 每度俯仰对应的像素：球体内约 120° 的可见范围铺满直径 */
  const pixelsPerDegree = (radius * 2) / 120;

  const apply = (frame: DroneTelemetry) => {
    const roll = clamp(frame.attitude.rollDeg, -180, 180);
    const pitch = clamp(frame.attitude.pitchDeg, -90, 90);
    const yaw = normalize360(frame.attitude.yawDeg);

    ballRef.current?.setAttribute('transform', `rotate(${(-roll).toFixed(2)} ${half} ${half})`);
    pitchRef.current?.setAttribute('transform', `translate(0 ${(pitch * pixelsPerDegree).toFixed(2)})`);
    needleRef.current?.setAttribute('transform', `rotate(${yaw.toFixed(2)} ${half} ${half})`);

    if (yawTextRef.current) yawTextRef.current.textContent = `${yaw.toFixed(0)}°`;
    if (rollTextRef.current) rollTextRef.current.textContent = formatSigned(roll, 1);
    if (pitchTextRef.current) pitchTextRef.current.textContent = formatSigned(pitch, 1);
    if (rateTextRef.current) rateTextRef.current.textContent = formatSigned(frame.attitude.yawRateDegS, 1);
  };

  useTelemetryAnimationFrame(droneId, apply);

  useEffect(() => {
    if (telemetry) apply(telemetry);
    // apply 只写 DOM，无需要进入依赖列表
  }, [telemetry]);

  const bandTop = half - radius * 2;
  const bandBottom = half;

  return (
    <div className={['flex flex-col items-center', className].join(' ')}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label="无人机姿态球"
        className="shrink-0"
      >
        <defs>
          <clipPath id="havorix-attitude-clip">
            <circle cx={half} cy={half} r={radius} />
          </clipPath>
          <linearGradient id="havorix-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0f4c9c" />
            <stop offset="100%" stopColor="#3f8ae8" />
          </linearGradient>
          <linearGradient id="havorix-ground" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8a5a2b" />
            <stop offset="100%" stopColor="#4a2f16" />
          </linearGradient>
        </defs>

        <g clipPath="url(#havorix-attitude-clip)">
          {/* 球体：整体按横滚旋转，内部再按俯仰平移地平线 */}
          <g ref={ballRef}>
            <g ref={pitchRef}>
              <rect x={-size} y={bandTop} width={size * 3} height={radius * 2} fill="url(#havorix-sky)" />
              <rect
                x={-size}
                y={bandBottom}
                width={size * 3}
                height={radius * 2}
                fill="url(#havorix-ground)"
              />
              <g stroke="#ffffff" strokeOpacity="0.6" strokeWidth="1.5">
                {[-30, -20, -10, 10, 20, 30].map((degree) => {
                  const y = half - degree * pixelsPerDegree;
                  const width = Math.abs(degree) % 20 === 0 ? 54 : 30;
                  return <line key={degree} x1={half - width / 2} y1={y} x2={half + width / 2} y2={y} />;
                })}
              </g>
              <line x1={-size} y1={half} x2={size * 2} y2={half} stroke="#ffffff" strokeWidth="2.5" />
            </g>
          </g>
        </g>

        {/* 固定的机体参考线：飞机符号不随姿态移动 */}
        <path
          d={`M ${half - 52} ${half} L ${half - 14} ${half} L ${half} ${half + 11} L ${half + 14} ${half} L ${half + 52} ${half}`}
          fill="none"
          stroke="#ffd400"
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={half} cy={half} r={3.5} fill="#ffd400" />

        {/* 外圈与横滚刻度 */}
        <g stroke="var(--line-strong)" strokeWidth="1.5">
          {[-60, -45, -30, -15, 15, 30, 45, 60].map((degree) => {
            const angle = ((degree - 90) * Math.PI) / 180;
            const inner = radius - 2;
            const outer = radius - (Math.abs(degree) % 30 === 0 ? 14 : 9);
            return (
              <line
                key={degree}
                x1={half + Math.cos(angle) * inner}
                y1={half + Math.sin(angle) * inner}
                x2={half + Math.cos(angle) * outer}
                y2={half + Math.sin(angle) * outer}
              />
            );
          })}
        </g>
        <circle cx={half} cy={half} r={radius} fill="none" stroke="var(--line-strong)" strokeWidth="2" />

        {/* 航向指针 */}
        <g ref={needleRef}>
          <line x1={half} y1={4} x2={half} y2={radius - 30} stroke="var(--accent)" strokeWidth="1.5" />
          <path d={`M ${half} ${2} L ${half - 6} ${16} L ${half + 6} ${16} Z`} fill="var(--accent)" />
        </g>

        <text x={half} y={radius - 26} textAnchor="middle" fill="#ffffff" fontSize={13} fontWeight={600}>
          <tspan ref={yawTextRef}>0°</tspan>
        </text>
      </svg>

      <dl className="mt-2 grid w-full grid-cols-3 gap-2 text-center">
        <div className="flex flex-col items-center gap-0.5 rounded border border-line bg-surface-1 px-1 py-1">
          <dt className="text-[10px] text-fg-subtle">横滚 (°)</dt>
          <dd className="text-xs font-semibold text-fg">
            <span ref={rollTextRef} className="tabular">
              --
            </span>
          </dd>
        </div>
        <div className="flex flex-col items-center gap-0.5 rounded border border-line bg-surface-1 px-1 py-1">
          <dt className="text-[10px] text-fg-subtle">俯仰 (°)</dt>
          <dd className="text-xs font-semibold text-fg">
            <span ref={pitchTextRef} className="tabular">
              --
            </span>
          </dd>
        </div>
        <div className="flex flex-col items-center gap-0.5 rounded border border-line bg-surface-1 px-1 py-1">
          <dt className="text-[10px] text-fg-subtle">偏航角速度 (°/s)</dt>
          <dd className="text-xs font-semibold text-fg">
            <span ref={rateTextRef} className="tabular">
              --
            </span>
          </dd>
        </div>
      </dl>
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(max, Math.max(min, value));
}

function normalize360(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return ((value % 360) + 360) % 360;
}

function formatSigned(value: number, digits: number): string {
  if (!Number.isFinite(value)) return '--';
  const rounded = Number(value.toFixed(digits));
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(digits)}`;
}
